<main>
  <loading-overlay [isLoading]="isLoading() > 0" />
  <p-toast position="top-center" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'ExpenseClaim_Title' | translateWF: 'Expense claims' }}</h1>
        <div class="page__title_buttons">
          <p-button [label]="'ExpenseClaim_New' | translateWF: 'New expense claim'" icon="pi pi-plus" (click)="createClaim()" />
        </div>
      </div>
      <p class="text-sm text-[var(--p-text-muted-color)]">
        {{ 'ExpenseClaim_Subtitle' | translateWF: 'The receipts of a fund or trip, sent together for approval and then to Accounting.' }}
      </p>
    </div>

    <div class="cs-summary-grid">
      <p-card data-variant="summary" [header]="'ExpenseClaim_Kpi_Draft' | translateWF: 'Drafts'">
        <ng-template pTemplate="subtitle">{{ summary()?.draft ?? 0 }}</ng-template>
        {{ 'ExpenseClaim_Kpi_Draft_Unit' | translateWF: 'being prepared' }}
      </p-card>
      <p-card data-variant="summary" data-severity="warning" [header]="'ExpenseClaim_Kpi_ToApprove' | translateWF: 'To approve'">
        <ng-template pTemplate="subtitle">{{ summary()?.toApprove ?? 0 }}</ng-template>
        {{ 'ExpenseClaim_Kpi_ToApprove_Unit' | translateWF: 'waiting for you' }}
      </p-card>
      <p-card data-variant="summary" [header]="'ExpenseClaim_Kpi_InApproval' | translateWF: 'In approval'">
        <ng-template pTemplate="subtitle">{{ summary()?.inApproval ?? 0 }}</ng-template>
        {{ 'ExpenseClaim_Kpi_InApproval_Unit' | translateWF: 'with their approvers' }}
      </p-card>
      <p-card data-variant="summary" [header]="'ExpenseClaim_Kpi_AccountingReview' | translateWF: 'Accounting review'">
        <ng-template pTemplate="subtitle">{{ summary()?.accountingReview ?? 0 }}</ng-template>
        {{ 'ExpenseClaim_Kpi_AccountingReview_Unit' | translateWF: 'approved, waiting for posting' }}
      </p-card>
    </div>

    <div class="cs-page-list-grid">
      @if (showFilters() && filterConfig(); as config) {
        <div>
          <csl-dynamic-filters [filterConfig]="config" [state]="filterState" />
        </div>
      }

      <p-panel [header]="'ExpenseClaim_Title' | translateWF: 'Expense claims'">
        <ng-template pTemplate="icons">
          <p-button
            [label]="(showFilters() ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters() ? 'Hide Filters' : 'Show Filters')"
            [icon]="showFilters() ? 'pi pi-filter-slash' : 'pi pi-filter'"
            (click)="showFilters.set(!showFilters())"
            variant="outlined"
            size="small"
          />
        </ng-template>
        <p-card data-variant="compact">
          <p-tabs [value]="scope()" (valueChange)="setScope($event)">
            <p-tablist>
              <p-tab [value]="scopes.All">{{ 'ExpenseClaim_Tab_All' | translateWF: 'All' }}</p-tab>
              <p-tab [value]="scopes.Mine">{{ 'ExpenseClaim_Tab_Mine' | translateWF: 'My claims' }}</p-tab>
              <p-tab [value]="scopes.ToApprove">
                {{ 'ExpenseClaim_Tab_ToApprove' | translateWF: 'To approve' }}
                @if ((summary()?.toApprove ?? 0) > 0) {
                  <p-tag [value]="(summary()?.toApprove ?? 0).toString()" severity="warn" />
                }
              </p-tab>
            </p-tablist>
          </p-tabs>

          <p-table [value]="items()" dataKey="id" [lazy]="true" (onSort)="onSort($event)" [rowHover]="true">
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="number" style="width: 14rem">
                  {{ 'ExpenseClaim_Col_Number' | translateWF: 'Number' }}
                  <p-sortIcon field="number" />
                </th>
                <th pSortableColumn="owner">
                  {{ 'ExpenseClaim_Field_Owner' | translateWF: 'Fund or trip' }}
                  <p-sortIcon field="owner" />
                </th>
                <th>{{ 'ExpenseClaim_Field_Description' | translateWF: 'Description' }}</th>
                <th class="text-right" style="width: 7rem">{{ 'ExpenseClaim_Col_Receipts' | translateWF: 'Receipts' }}</th>
                <th pSortableColumn="total" class="text-right" style="width: 12rem">
                  {{ 'ExpenseClaim_Col_Total' | translateWF: 'Total' }}
                  <p-sortIcon field="total" />
                </th>
                <th class="text-right" style="width: 9rem">{{ 'ExpenseClaim_Col_TotalUsd' | translateWF: 'Total USD' }}</th>
                <th pSortableColumn="status" style="width: 11rem">
                  {{ 'Status' | translateWF: 'Status' }}
                  <p-sortIcon field="status" />
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-rowData>
              @let row = getRow(rowData);
              <tr>
                <td>
                  <a class="cs-table-link font-mono" (click)="redirectToDetails(row.id)">{{ row.number }}</a>
                </td>
                <td>
                  <div class="flex items-center gap-2">
                    <i [class]="row.owner.entityDefinitionCode === ownerCodes.Trip ? 'pi pi-briefcase' : 'pi pi-wallet'" class="text-surface-500" aria-hidden="true"></i>
                    <span class="flex flex-col">
                      <span>{{ row.ownerName }}</span>
                      <small class="text-surface-500 font-mono">{{ row.owner.number }}</small>
                    </span>
                  </div>
                </td>
                <td>{{ row.description }}</td>
                <td class="text-right tabular-nums">{{ row.receipts.length }}</td>
                <td class="text-right tabular-nums">{{ symbolOf(row.currency) }} {{ row.total | number: '1.2-2' }}</td>
                <td class="text-right tabular-nums">$ {{ row.totalUsd | number: '1.2-2' }}</td>
                <td>
                  <p-tag [severity]="statusSeverity(row.status)" [value]="statusLabel(row.status)" />
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="7" class="text-center">{{ 'ExpenseClaim_Empty' | translateWF: 'No expense claims found.' }}</td>
              </tr>
            </ng-template>
          </p-table>
          <p-paginator
            [rows]="pageSize()"
            [totalRecords]="totalRecords()"
            [first]="(currentPage() - 1) * pageSize()"
            (onPageChange)="onPageChange($event)"
            [rowsPerPageOptions]="[25, 50, 100]"
          />
        </p-card>
      </p-panel>
    </div>
  </div>
</main>
